import { saveAs } from 'file-saver'
import { unparse } from 'papaparse'
import { LabelOption } from 'src/@types'
import { AnimalProps } from 'src/@types/Animal'

// Exporta utilitários de peso
export * from './weightFormatters'

export const concatStyles = (value1: any, value2: any) => {
  if (value2) {
    return `${value1} ${value2}`
  }
  return value1
}

export const formaterEmail = (value: string) => {
  if (value) {
    return value
      ?.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[,<|>:;"'{}[=()*&^%$#!~`?]/g, '')
  }
  return value
}

export const onlyInt = (value: string) => {
  const newValue = value?.replace(/\D/g, '')
  if (value && +newValue >= 0) return +newValue
  return undefined
}

export const cpfMask = (value: string) => {
  if (value) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }
  return value
}

export const isValidCPF = (cpf: string) => {
  if (!cpf) return false

  const cleanCpf = cpf.replace(/\D/g, '')

  if (cleanCpf.length !== 11) return false

  if (/^(\d)\1+$/.test(cleanCpf)) return false

  const calcDigit = (base: string, factor: number) => {
    let total = 0

    for (let i = 0; i < base.length; i++) {
      total += Number(base[i]) * factor--
    }

    const rest = total % 11
    return rest < 2 ? 0 : 11 - rest
  }

  const digit1 = calcDigit(cleanCpf.slice(0, 9), 10)
  const digit2 = calcDigit(cleanCpf.slice(0, 10), 11)

  return digit1 === Number(cleanCpf[9]) && digit2 === Number(cleanCpf[10])
}

export const cnpjMask = (value: string) => {
  if (value) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }
  return value
}

export const phoneMask = (value: string) => {
  if (value) {
    if (value.length <= 14) {
      return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{4})(\d)/, '$1')
    }
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})(\d)/, '$1')
  }
  return value
}

export const zipCodeMask = (value: string) => {
  if (value) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{3})(\d)/, '$1')
  }
  return value
}

export const getLabel = (object: LabelOption, keys: string[]): string => {
  let words = ''
  const final = keys.length
  keys.forEach((key, index) => {
    if (index + 1 < final) {
      if (object?.[key] && object?.[key] !== '') {
        words += `${object?.[key] || ''} - `
      }
    } else {
      words += object?.[key] || ''
    }
  })
  return words
}

export const textUpperCase = (value: string) => {
  if (value) {
    return value
      ?.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[.,<|>:;"'{}[=_()*&^%$#@!~`?]/g, '')
      .toUpperCase()
  }
  return value
}

export const formatDate = (dateString: string | null, includeTime = false) => {
  if (!dateString) return

  const date = new Date(dateString)

  return date.toLocaleString('pt-BR', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(includeTime && {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  })
}

export function formatToBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export const exportToCsv = (data: any[], columns: { [key: string]: string }, fileName: string) => {
  const csvData = data.map((item: any) => {
    const formattedItem: { [key: string]: any } = {}
    Object.keys(columns).forEach((key) => {
      formattedItem[columns[key]] = item[key]
    })
    return formattedItem
  })

  const csv = unparse(csvData, {
    delimiter: ';'
  })
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  saveAs(blob, `${fileName}.csv`)
}

export const formatDateToInputValue = (date: string | Date | null | undefined) => {
  if (!date) return ''

  // Converter para string se não for
  const dateStr = typeof date === 'string' ? date : date.toString()

  if (dateStr.includes('T')) {
    return dateStr.split('T')[0]
  }
  return dateStr.split(' ')[0]
}

export function formatedAnimalListSelect(animalList: AnimalProps[], sex?: string) {
  const filteredList = animalList.filter((item) => item.idAnimal && (!sex || item.sexo === sex))

  return filteredList.map((item) => ({
    id: item.idAnimal!,
    value: `${item.idAnimal}-${item.nome || 'Sem nome'}`,
    animalName: item.nome
  }))
}

export const formatCurrencyValue = (value: string) => {
  const cleanValue = value.replace(/\D/g, '')
  const formattedValue = (Number(cleanValue) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
  return formattedValue
}

export function convertCurrencyToNumber(value: string) {
  const formattedValue = value.replace(/[R$\s]/g, '').replace(',', '.')
  return parseFloat(formattedValue)
}

export const formatCurrencyValueDirect = (value: string) => {
  // Para valores que já vem com decimal correto
  const cleanValue = value.replace(/[R$\s]/g, '').replace(',', '.')
  const numericValue = parseFloat(cleanValue) || 0

  return numericValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
}

export const getDataTableToExport = (data: any[]) => {
  const event = new CustomEvent('exportData')
  window.dispatchEvent(event)
  return data
}

export const formatWeightAnimal = (value: string | number): string => {
  if (!value) return ''

  // Converte para string e remove espaços extras
  let stringValue = String(value).trim()

  // Remove caracteres inválidos (exceto números, pontos e vírgulas)
  stringValue = stringValue.replace(/[^\d.,]/g, '')

  // Se já estiver no formato correto, retorna sem modificar
  if (/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(stringValue)) {
    return `${stringValue} KG`
  }

  // Se houver múltiplas vírgulas, mantém apenas a última como decimal
  const parts = stringValue.split(',')
  if (parts.length > 1) {
    const intPart = parts.slice(0, -1).join('') // Junta todas as partes antes da última vírgula
    const decimalPart = parts[parts.length - 1] // Última parte como decimal
    stringValue = `${intPart}.${decimalPart}` // Substitui a última vírgula por ponto
  } else {
    stringValue = stringValue.replace(',', '.')
  }

  // Converte para número
  const numericValue = parseFloat(stringValue)

  // Retorna vazio se o número for inválido
  if (isNaN(numericValue)) return ''

  // Mantém o separador de milhar original (se já estava correto, não altera)
  const formattedValue = numericValue.toLocaleString('pt-BR', {
    minimumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 3
  })

  return `${formattedValue} KG`
}
export const calculateBirthForecast = (
  diagnosisDate: string | null,
  gestationTime: number | null,
  reproductionDate: string | null
): string | null => {
  // Se não houver data de diagnóstico ou reprodução, retorna null
  if (!diagnosisDate && !reproductionDate) {
    return null
  }

  const baseDate = diagnosisDate || reproductionDate
  if (!baseDate) {
    return null
  }

  try {
    const date = new Date(baseDate)

    // Calcula os dias a serem adicionados
    // Se há tempo de gestação, subtrai de 280, caso contrário usa 280
    const daysToAdd = gestationTime && gestationTime > 0 ? 280 - gestationTime : 280

    // Adiciona os dias à data
    date.setDate(date.getDate() + daysToAdd)

    // Retorna a data em formato ISO (YYYY-MM-DD)
    return date.toISOString().split('T')[0]
  } catch (error) {
    console.error('Erro ao calcular previsão de parto:', error)
    return null
  }
}
